import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import http from '@/services/http';
import { showWarning } from '@/utils/toast';

interface PriorityTheme {
    bg: string;
    border: string;
    title: string;
    text: string;
    icon: string;
    button: string;
    outline_button: string;
}

interface Announcement {
    id: number;
    title: string;
    content: string;
    content_snippet: string;
    priority_icon: string;
    priority_theme: PriorityTheme;
}

type PendingAction = 'view' | 'snooze' | 'dismiss' | null;

export const AnnouncementBanner = () => {
    const { t } = useTranslation('admin');
    const [announcement, setAnnouncement] = useState<Announcement | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [pending, setPending] = useState<PendingAction>(null);

    const loadNext = useCallback(async () => {
        try {
            const response = await http.get<{ data: Announcement | null }>('/api/admin/announcements/active');
            setAnnouncement(response.data.data);
        } catch (error) {
            console.error('Failed to load announcements', error);
        }
    }, []);

    useEffect(() => {
        void loadNext();
    }, [loadNext]);

    const view = async () => {
        if (!announcement) return;
        setPending('view');
        try {
            await http.post(`/api/admin/announcements/${announcement.id}/view`);
            setShowModal(true);
        } finally {
            setPending(null);
        }
    };

    const dismiss = async () => {
        if (!announcement) return;
        setPending('dismiss');
        try {
            await http.post(`/api/admin/announcements/${announcement.id}/dismiss`);
        } finally {
            setPending(null);
            setShowModal(false);
            void loadNext();
        }
    };

    const snooze = async () => {
        if (!announcement) return;
        setPending('snooze');
        try {
            await http.post(`/api/admin/announcements/${announcement.id}/snooze`);
            showWarning(t('common.announcements.banner.snoozed_toast'));
        } finally {
            setPending(null);
            setShowModal(false);
            void loadNext();
        }
    };

    if (!announcement) {
        return null;
    }

    const busy = pending !== null;
    const theme = announcement.priority_theme;

    return (
        <>
            <div
                className={`flex flex-col items-start justify-between gap-4 border-b px-4 py-3 sm:flex-row sm:items-center ${theme.bg} ${theme.border}`}
            >
                <div className="flex min-w-0 flex-1 items-start gap-2">
                    <i className={`fas ${announcement.priority_icon} mt-1 ${theme.icon}`} />
                    <div className="min-w-0 flex-1">
                        <div className={`font-semibold ${theme.title}`}>{announcement.title}</div>
                        <div className={`truncate text-sm ${theme.text}`}>{announcement.content_snippet}</div>
                    </div>
                </div>
                <div className={`flex shrink-0 items-center gap-2 ${busy ? 'opacity-60' : ''}`}>
                    <button
                        type="button"
                        onClick={() => void view()}
                        disabled={busy}
                        className={`rounded-md px-3 py-1.5 text-sm text-white disabled:cursor-wait ${theme.button}`}
                    >
                        {pending === 'view' ? (
                            <>
                                <i className="fas fa-spinner fa-spin" /> {t('common.announcements.banner.view_loading')}
                            </>
                        ) : (
                            t('common.announcements.banner.view')
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={() => void snooze()}
                        disabled={busy}
                        className={`rounded-md border px-3 py-1.5 text-sm disabled:cursor-wait ${theme.outline_button}`}
                    >
                        {pending === 'snooze' ? (
                            <>
                                <i className="fas fa-spinner fa-spin" /> {t('common.announcements.banner.snoozing')}
                            </>
                        ) : (
                            t('common.announcements.banner.snooze')
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={() => void dismiss()}
                        disabled={busy}
                        className={`rounded-md border px-3 py-1.5 text-sm disabled:cursor-wait ${theme.outline_button}`}
                    >
                        {pending === 'dismiss' ? (
                            <>
                                <i className="fas fa-spinner fa-spin" /> {t('common.announcements.banner.marking')}
                            </>
                        ) : (
                            t('common.announcements.banner.mark_as_read')
                        )}
                    </button>
                </div>
            </div>

            {showModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                    onClick={(e) => e.target === e.currentTarget && setShowModal(false)}
                >
                    <div className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-lg">
                        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                            <h3 className="text-lg font-semibold text-gray-900">{announcement.title}</h3>
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <i className="fas fa-times" />
                            </button>
                        </div>
                        <div className="prose px-6 py-4" dangerouslySetInnerHTML={{ __html: announcement.content }} />
                        <div className="flex justify-end gap-2 border-t border-gray-200 px-6 py-4">
                            <button
                                type="button"
                                onClick={() => void snooze()}
                                disabled={busy}
                                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-wait"
                            >
                                {pending === 'snooze'
                                    ? t('common.announcements.banner.snoozing')
                                    : t('common.announcements.banner.snooze')}
                            </button>
                            <button
                                type="button"
                                onClick={() => void dismiss()}
                                disabled={busy}
                                className={`rounded-md px-4 py-2 text-sm font-medium text-white disabled:cursor-wait ${theme.button}`}
                            >
                                {pending === 'dismiss'
                                    ? t('common.announcements.banner.marking')
                                    : t('common.announcements.banner.mark_as_read')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
